import React, { useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import * as XLSX from 'xlsx';

const COLUMN_MAPPING = {
  'description': 'description', 'descripti': 'description',
  'total hours': 'total_hours', 'total hour': 'total_hours', 'total_hours': 'total_hours',
  'total install': 'total_install', 'total inst': 'total_install', 'total_install': 'total_install',
  'hourly': 'hourly',
  'daily': 'daily',
  'weekly': 'weekly',
  'monthly': 'monthly',
  'days/wk': 'days_wk', 'days_wk': 'days_wk', 'days wk': 'days_wk',
  'hrs/day': 'hrs_day', 'hrs_day': 'hrs_day', 'hrs day': 'hrs_day',
  'qty': 'qty', 'quantity': 'qty',
  'period type': 'period_type', 'period ty': 'period_type', 'period_type': 'period_type',
  'duration': 'duration', 'dur': 'duration', 'dur.': 'duration',
  'basic rental': 'basic_rental', 'basic ren': 'basic_rental', 'basic_rental': 'basic_rental',
  'fuel factor': 'fuel_factor', 'fuel fact': 'fuel_factor', 'fuel_factor': 'fuel_factor',
  'fuel cost': 'fuel_cost', 'fuel_cost': 'fuel_cost',
  'mob/dem': 'mob_dem', 'mob_dem': 'mob_dem', 'mob dem': 'mob_dem',
  'surcharge factor': 'surcharge_factor', 'surch. fac': 'surcharge_factor', 'surcharge_factor': 'surcharge_factor', 'surch fac': 'surcharge_factor',
  'surcharge': 'surcharge', 'surch.': 'surcharge', 'surch': 'surcharge', 'surcharg': 'surcharge',
  'purchase': 'purchase', 'purchas': 'purchase',
  'total cost': 'total_cost', 'total cos': 'total_cost', 'total_cost': 'total_cost',
  'site expense': 'site_expense', 'site expen': 'site_expense', 'site_expense': 'site_expense',
  'source': 'source',
  'trade': 'trade',
  'from out': 'from_out', 'from_out': 'from_out',
  'subject to': 'subject_to', 'subject_to': 'subject_to',
  'comments': 'comments'
};

const RentalsUploadModal = ({ onClose, onUploadComplete }) => {
  const { instance, accounts } = useMsal();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setError(null);
    try {
      const data = await f.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      
      const mapped = json.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          const lowerKey = key.toLowerCase().trim();
          const dbKey = COLUMN_MAPPING[lowerKey];
          if (dbKey) newRow[dbKey] = row[key];
        });
        return newRow;
      });
      setPreview(mapped.slice(0, 5));
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      
      const mapped = json.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          const lowerKey = key.toLowerCase().trim();
          const dbKey = COLUMN_MAPPING[lowerKey];
          if (dbKey) newRow[dbKey] = row[key];
        });
        return newRow;
      });
      
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/rentals/upload', { data: mapped });
      
      if (response.data.success) {
        alert(`Successfully imported ${response.data.rowsProcessed} records`);
        onUploadComplete();
        onClose();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#2a2a3a', padding: '30px', borderRadius: '8px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>Import Rentals</h3>
        <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" onChange={handleFileChange} style={{ marginBottom: '20px', color: '#fff' }} />
        {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}
        {preview.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: '#fff' }}>Preview (first 5 rows):</h4>
            <div style={{ overflow: 'auto', maxHeight: '200px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>{Object.keys(preview[0]).map(k => <th key={k} style={{ backgroundColor: '#343a40', color: '#fff', padding: '6px', borderBottom: '1px solid #4a4a5a' }}>{k}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => <tr key={i}>{Object.values(row).map((v, j) => <td key={j} style={{ padding: '6px', borderBottom: '1px solid #3a3a4a', color: '#ccc' }}>{String(v).substring(0, 30)}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleUpload} disabled={!file || uploading} style={{ padding: '10px 20px', backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{uploading ? 'Importing...' : 'Import'}</button>
        </div>
      </div>
    </div>
  );
};

export default RentalsUploadModal;
